import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
} from "firebase/auth";
import { auth } from "../firebase";
import { verifyClient } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

type Step = "phone" | "code" | "denied";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
  }
}

export default function Login() {
  const navigate = useNavigate();
  const { refreshClaims } = useAuth();
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("+1");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const confirmation = useRef<ConfirmationResult | null>(null);

  // Set up invisible reCAPTCHA once
  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        { size: "invisible" }
      );
    }
  }, []);

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!phone.startsWith("+")) {
      setError("Enter your phone in international format, like +13305551234.");
      return;
    }
    setBusy(true);
    try {
      const verifier = window.recaptchaVerifier!;
      confirmation.current = await signInWithPhoneNumber(auth, phone, verifier);
      setStep("code");
    } catch (err) {
      console.error(err);
      setError(
        "We couldn't send a code to that number. Double-check the format and try again."
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!confirmation.current) return;
    setBusy(true);
    try {
      // Step 1: complete the OTP — this signs the user in
      await confirmation.current.confirm(code);
      // Step 2: ask the backend whether this phone is a real client
      const result = await verifyClient({});
      if (!result.ok) {
        await auth.signOut();
        setStep("denied");
        return;
      }
      // Step 3: refresh ID token to pick up the new custom claims
      await refreshClaims();
      // wait a tick so React reads the updated context before nav
      setTimeout(() => navigate("/"), 50);
    } catch (err) {
      console.error(err);
      setError("That code didn't match. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      {/* Top brand */}
      <div className="px-5 sm:px-10 pt-5 sm:pt-6 pb-2 sm:pb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img
            src="/pronerdz-logo.png"
            alt="ProNerdz"
            className="h-12 sm:h-14 w-auto"
          />
          <span className="hidden sm:inline font-mono text-[10px] uppercase text-ink-400">
            client portal
          </span>
        </div>
        <a
          href="https://pronerdz.com"
          className="text-xs text-ink-500 hover:text-ink-900"
        >
          ← back to pronerdz.com
        </a>
      </div>

      {/* Form area: anchors to top on mobile (no big empty space above the
          form), centers vertically on tablets+ where there's room for it. */}
      <div className="flex-1 flex items-start sm:items-center justify-center px-5 sm:px-6 pt-4 pb-8 sm:py-12">
        <div className="w-full max-w-md">
          {/* Hero */}
          <div className="mb-6 sm:mb-8">
            <div className="label mb-2 sm:mb-3">Sign in</div>
            <h1 className="display text-3xl sm:text-4xl font-semibold leading-tight">
              {step === "denied"
                ? "Account not found"
                : step === "code"
                ? "Check your phone"
                : "Welcome back"}
            </h1>
            <p className="mt-3 text-ink-500">
              {step === "phone" &&
                "Enter the phone number on file with ProNerdz. We'll text you a one-time code — no password needed."}
              {step === "code" &&
                `We sent a 6-digit code to ${phone}. It usually arrives within a few seconds.`}
              {step === "denied" &&
                "We couldn't find a ProNerdz account connected to this phone number. Please contact ProNerdz for access."}
            </p>
          </div>

          {step === "phone" && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label htmlFor="phone" className="label block mb-1.5">
                  Phone number
                </label>
                <input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+1 330 555 1234"
                  className="input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
                <p className="mt-1.5 text-xs text-ink-400">
                  Use international format including country code.
                </p>
              </div>
              {error && (
                <div className="rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-800">
                  {error}
                </div>
              )}
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={busy}
              >
                {busy ? "Sending…" : "Send code"}
              </button>
            </form>
          )}

          {step === "code" && (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label htmlFor="code" className="label block mb-1.5">
                  Verification code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  maxLength={6}
                  className="input font-mono text-lg tracking-[0.5em]"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  required
                />
              </div>
              {error && (
                <div className="rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-800">
                  {error}
                </div>
              )}
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={busy || code.length !== 6}
              >
                {busy ? "Verifying…" : "Verify and sign in"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep("phone");
                  setCode("");
                  setError(null);
                }}
                className="btn-ghost w-full"
              >
                Use a different number
              </button>
            </form>
          )}

          {step === "denied" && (
            <div className="space-y-4">
              <div className="rounded-xl border border-ink-100 bg-white p-5">
                <div className="text-sm text-ink-700">
                  If you believe this is a mistake, reach out and we'll get
                  things sorted.
                </div>
                <a
                  href="mailto:help@pronerdz.com"
                  className="mt-3 inline-block text-sm font-medium text-ink-900 underline underline-offset-4"
                >
                  help@pronerdz.com →
                </a>
              </div>
              <button
                onClick={() => {
                  setStep("phone");
                  setPhone("+1");
                  setCode("");
                  setError(null);
                }}
                className="btn-outline w-full"
              >
                Try another number
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
